import { Badge } from "design-system/atoms/Badge"
import { Button } from "design-system/atoms/Button"
import { Sheet, SheetContent } from "design-system/atoms/Sheet"
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "design-system/atoms/Tooltip"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "design-system/molecules/Tabs"
import { cn } from "design-system/utils"
import { AnimatePresence, motion } from "framer-motion"
import {
  Award,
  Eye,
  EyeOff,
  FlaskConical,
  Headset,
  MessageSquareText,
  NotebookPen,
  ShieldAlert,
  User,
} from "lucide-react"
import { useAuthEmployeeQuery } from "modules/auth/hooks/useAuthEmployeeQuery"
import { useNewSupportFlow } from "modules/auth/hooks/useNewSupportFlow"
import { ChatHistory } from "modules/conversations/components/ChatHistory"
import { OpportunitiesTimeline } from "modules/conversations/components/OpportunitiesTimeline"
import { CustomerInfo } from "modules/customers/components/Detail/CustomerInfo"
import { Developer } from "modules/customers/components/Detail/Developer"
import { Profile } from "modules/customers/components/Detail/Profile"
import { useCustomerQuery } from "modules/customers/hooks/useCustomerQuery"
import { CustomerNotes } from "modules/notes/components/CustomerNotes"
import { Loading } from "modules/shared/components/Loading"
import { useDeveloperMode } from "modules/shared/hooks/useDeveloperMode"
import { SupportTimeline } from "modules/support/components/SupportTimeline"
import { CustomerIssues } from "modules/support/components/entities/CustomerIssues"
import { useEffect, useMemo, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { BackofficeType } from "schema"

export type CustomerDetailTabEnum =
  "conversations" | "customer" | "support" | "issues" | "opportunities" | "notes" | "developer"

type Props = {
  customerId: string
  conversationId?: string | null
  noteId?: string
  campaignId?: string
  questionId?: string
  issueId?: string
  onClose: () => void
  currentTab: CustomerDetailTabEnum
  onTabChange: (tab: CustomerDetailTabEnum) => void
  onConversationClick: (conversationId?: string) => void
  onIssueClick: (issueId: string, tab: "issues" | "support") => void
  onArchivedCustomerRedirect?: (customerId: string) => void
}

export function CustomerDetail({
  customerId,
  conversationId,
  noteId,
  campaignId,
  questionId,
  issueId,
  onClose,
  currentTab,
  onTabChange,
  onConversationClick,
  onIssueClick,
  onArchivedCustomerRedirect,
}: Props) {
  const { t } = useTranslation()
  const { data: employee } = useAuthEmployeeQuery()
  const { developerMode } = useDeveloperMode()
  const { enabledAt: newSupportFlowEnabledAt, isEnabled: isNewSupportFlowEnabled } =
    useNewSupportFlow()
  const tabsListRef = useRef<HTMLDivElement | null>(null)
  const [showMobileCustomerDetails, setShowMobileCustomerDetails] = useState(false)

  const { data: customer, isPending: isCustomerPending } = useCustomerQuery({ id: customerId })
  const isPending = isCustomerPending || customer?.id !== customerId
  const canSeeDeveloperTab = developerMode && employee?.backofficeType === BackofficeType.Superadmin
  const canSeeIssuesTab = developerMode && !isNewSupportFlowEnabled

  useEffect(() => {
    setShowMobileCustomerDetails(false)
  }, [currentTab, customerId])

  useEffect(() => {
    if (!employee || currentTab !== "developer" || canSeeDeveloperTab) return

    onTabChange("conversations")
  }, [canSeeDeveloperTab, currentTab, employee, onTabChange])

  useEffect(() => {
    if (currentTab !== "issues" || canSeeIssuesTab) return

    onTabChange(isNewSupportFlowEnabled ? "support" : "conversations")
  }, [canSeeIssuesTab, currentTab, isNewSupportFlowEnabled, onTabChange])

  useEffect(() => {
    if (!customer?.archivedAt || !customer.originalCustomerId) return
    if (customer.originalCustomerId === customerId) return

    onArchivedCustomerRedirect?.(customer.originalCustomerId)
  }, [customer, customerId, onArchivedCustomerRedirect])

  const customerInfo = useMemo(() => {
    if (isPending) {
      return <Loading />
    }

    if (!customer) {
      throw new Error("Customer not found")
    }

    const customerHasName = !!customer.name?.length
    const shouldCollapseCustomerInfo = currentTab === "conversations"

    return (
      <div className="flex flex-col gap-2">
        <div className="w-full flex flex-row items-start justify-between gap-3">
          <div className="flex flex-row flex-wrap items-center gap-4 min-w-0">
            <span
              className={cn("text-lg", customerHasName ? "font-medium" : "text-muted-foreground")}
            >
              {customerHasName ? customer.name : t("customers.profile.noName")}
            </span>
            {customer.isFake && (
              <Badge variant="secondary" className="text-background bg-gradient-testing">
                {t("customers.profile.fake")}
              </Badge>
            )}
            {customer.optedOutAt && (
              <Badge variant="destructive" className="text-background">
                {t("conversations.chat.customer.optedOut")}
              </Badge>
            )}
            {customer.denylistedAt && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Badge variant="destructive" className="text-background">
                      <ShieldAlert size={12} />
                      {t("conversations.chat.customer.blocked")}
                    </Badge>
                  </TooltipTrigger>
                  <TooltipContent>{t("conversations.chat.customer.blockedTooltip")}</TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
          </div>
          {shouldCollapseCustomerInfo && !customer.isFake && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="md:hidden"
              onClick={() => setShowMobileCustomerDetails((isVisible) => !isVisible)}
            >
              {showMobileCustomerDetails ? <EyeOff /> : <Eye />}
              {showMobileCustomerDetails
                ? t("conversations.chat.customer.hideDetails")
                : t("conversations.chat.customer.viewDetails")}
            </Button>
          )}
        </div>
        {!customer.isFake && (
          <div
            className={cn(
              shouldCollapseCustomerInfo && !showMobileCustomerDetails && "hidden md:block",
            )}
          >
            <CustomerInfo customer={customer} customerSentiment={customer.sentiment} />
          </div>
        )}
      </div>
    )
  }, [currentTab, customer, isPending, showMobileCustomerDetails, t])

  return (
    <Sheet
      open
      onOpenChange={(open) => {
        if (!open) {
          onClose()
        }
      }}
    >
      <SheetContent
        onInteractOutside={(event) => event.preventDefault()}
        side="right"
        className="w-full sm:max-w-screen-lg max-h-screen min-w-[320px]"
      >
        <AnimatePresence mode="sync">
          <motion.div
            key={customerId}
            initial={{ opacity: 0, y: 5 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -5 }}
            transition={{ duration: 0.2 }}
            className="flex flex-col gap-4 justify-items-start h-full"
          >
            {customerInfo}
            <Tabs
              className="flex flex-col flex-1 min-h-0"
              value={currentTab}
              onValueChange={(value) => onTabChange(value as CustomerDetailTabEnum)}
            >
              <TabsList className="flex justify-between gap-2 w-full">
                <div className="flex h-full min-w-0 overflow-x-auto overflow-y-hidden no-scrollbar [&>button]:flex-none">
                  <TabsTrigger value="customer">
                    <User className="size-4" />
                    <span>{t("customers.detail.profile")}</span>
                  </TabsTrigger>
                  <TabsTrigger value="conversations">
                    <MessageSquareText className="size-4" />
                    <span>{t("conversations.header.title")}</span>
                  </TabsTrigger>
                  <TabsTrigger value="support">
                    <Headset className="size-4" />
                    <span>{t("customers.detail.support")}</span>
                  </TabsTrigger>
                  {canSeeIssuesTab && (
                    <TabsTrigger
                      value="issues"
                      activeBorderColor="data-[state=active]:border-b-testing-1"
                    >
                      <Headset className="size-4 stroke-[var(--testing-1)]" />
                      <span className="text-gradient-testing">{t("customers.detail.issues")}</span>
                    </TabsTrigger>
                  )}
                  <TabsTrigger value="opportunities">
                    <Award className="size-4" />
                    <span>{t("customers.detail.opportunities")}</span>
                  </TabsTrigger>
                  <TabsTrigger value="notes">
                    <NotebookPen className="size-4" />
                    <span>{t("customers.detail.notes")}</span>
                  </TabsTrigger>
                  {canSeeDeveloperTab && (
                    <TabsTrigger
                      value="developer"
                      activeBorderColor="data-[state=active]:border-b-testing-1"
                    >
                      <FlaskConical className="size-4 stroke-[var(--testing-1)]" />
                      <span className="text-gradient-testing">
                        {t("customers.detail.developer")}
                      </span>
                    </TabsTrigger>
                  )}
                </div>
                <div className="flex h-full shrink-0 gap-2 items-center" ref={tabsListRef} />
              </TabsList>
              <TabsContent
                value="conversations"
                forceMount
                className="flex flex-col flex-1 min-h-0 data-[state=inactive]:hidden"
              >
                {customer && !isPending ? (
                  <ChatHistory
                    customer={customer}
                    conversationId={conversationId}
                    noteId={noteId}
                    campaignId={campaignId}
                    onConversationClick={onConversationClick}
                    onIssueClick={(selectedIssueId) =>
                      onIssueClick(selectedIssueId, isNewSupportFlowEnabled ? "support" : "issues")
                    }
                    tabsListRef={tabsListRef}
                  />
                ) : (
                  <Loading />
                )}
              </TabsContent>
              <TabsContent value="customer">
                {customer && !isPending ? (
                  <Profile customer={customer} isPending={isPending} actionsRef={tabsListRef} />
                ) : (
                  <Loading />
                )}
              </TabsContent>
              <TabsContent value="support" className="flex-1 min-h-0 overflow-y-auto">
                <SupportTimeline
                  customerId={customerId}
                  onConversationClick={onConversationClick}
                  selectedQuestionId={questionId}
                  createdBefore={newSupportFlowEnabledAt ?? undefined}
                  selectedIssueId={issueId}
                  shouldShowIssues={isNewSupportFlowEnabled}
                />
              </TabsContent>
              {canSeeIssuesTab && (
                <TabsContent value="issues" className="flex-1 min-h-0 overflow-y-auto">
                  <CustomerIssues
                    customerId={customerId}
                    selectedIssueId={issueId}
                    onConversationClick={onConversationClick}
                  />
                </TabsContent>
              )}
              <TabsContent value="opportunities" className="flex-1 min-h-0 overflow-y-auto">
                <OpportunitiesTimeline
                  customerId={customerId}
                  onConversationClick={onConversationClick}
                />
              </TabsContent>
              <TabsContent value="notes" className="flex-1 min-h-0 flex flex-col">
                <CustomerNotes customerId={customerId} />
              </TabsContent>
              {canSeeDeveloperTab && (
                <TabsContent value="developer" className="flex-1 min-h-0 overflow-y-auto">
                  <Developer customerId={customerId} conversationId={conversationId} />
                </TabsContent>
              )}
            </Tabs>
          </motion.div>
        </AnimatePresence>
      </SheetContent>
    </Sheet>
  )
}
